import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface FullscreenContentProps {
  children: React.ReactNode;
  translateY: SharedValue<number>;
  /** When true, skips safe-area padding and border radius (used inside React Navigation screens). */
  embedded?: boolean;
  /** When true on Android embedded, skips bottom safe-area padding (handled by keyboard spacer instead). */
  avoidKeyboard?: boolean;
}

export const FullscreenContent: React.FC<FullscreenContentProps> = ({
  children,
  translateY,
  embedded = false,
  avoidKeyboard = false,
}) => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();
  // On Android with edge-to-edge (RN 0.76+), useSafeAreaInsets() may return 0 for
  // embedded screens before native measurement completes. Use initialWindowMetrics fallback.
  const safeTop = Platform.OS === 'android'
    ? Math.max(insets.top, initialWindowMetrics?.insets?.top ?? 0)
    : insets.top;
  const safeBottom = Platform.OS === 'android'
    ? Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0)
    : insets.bottom;

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View style={[styles.animator, animatedStyle]}>
      <View
        style={[
          styles.container,
          { backgroundColor: colors.bg.modal },
          !embedded && styles.standalone,
          (!embedded || Platform.OS === 'android') && { paddingTop: safeTop },
          embedded && Platform.OS === 'android' && !avoidKeyboard && { paddingBottom: safeBottom },
        ]}
      >
        {children}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  animator: {
    flex: 1,
  },
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  standalone: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
});
